import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { TranslateModule, TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Subscription, firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AudienceApiService, AUDIENCE_TYPES, AudienceType, CountryOption, GoalOption, LOOKALIKE_METHODS, LookalikeMethod } from '../../../services/audience-api.service';
import { AuthService } from '../../../services/auth.service';
import { UserPermissions } from '../../../services/permissions';
import { NotificationService } from '../../../services/notification.service';
import { CustomSelectComponent, CustomSelectOption } from '../../shared/custom-select/custom-select.component';
import { findCompositionError, hasAccuracySelection, normalizeSubmissionRegions } from './audience-create-submission';
import { SearchClearDirective } from '../../shared/search-clear/search-clear.directive';

export type RuleGroupType = 'custom_import' | 'goal' | 'audience' | 'source';
export type LogicalOperator = 'AND' | 'OR' | 'AND NOT';
export type AudienceQuality = 'low' | 'medium' | 'high' | null;
export type AudienceFormStatus = 'active' | 'paused' | 'error' | 'pending' | 'draft';

export interface AudiencePreviewPointView {
  step: number;
  users: number;
  minDistance: number;
  maxDistance: number | null;
  label: string;
}

export interface AudiencePreviewAccuracyView {
  points: AudiencePreviewPointView[];
  defaultStep: number;
  selectedStep: number;
  suggestedStep?: number;
}

export interface AudienceCreateFormValue {
  type: AudienceType;
  name: string;
  description: string;
  frequency: string;
  startDate: string;
  endDate: string;
  ongoing: boolean;
  aiDescription: string;
  useAllNetworkData: boolean;
  expandSimilarUsers: boolean;
  // Modalità di espansione (admin-only): true => embedding pesati IDF ('weighted', default), false => 'standard'
  expandVersionWeighted: boolean;
  // Metodo del lookalike (admin-only) delle custom con espansione attiva
  lookalikeMethod: LookalikeMethod;
  ageRange: string[];
  gender: string[];
  location: string;
  regions: string[];
  audienceQuality: AudienceQuality;
  extendVolumes: number | null;
  aiTags: string[];
  ruleBlocks: Array<{
    operator: LogicalOperator;
    groups: Array<{
      type: RuleGroupType;
      valueOperator: LogicalOperator;
      payload: string[];
    }>;
  }>;
  previewAccuracyStep?: number | null;
  // true quando, in edit di un'audience "interests", uno dei filtri che determinano la curva
  // (tag, età, genere, paese, regioni) è diverso da com'era all'apertura della modale.
  interestFiltersChanged?: boolean;
  // true quando l'utente ha confermato di voler procedere con un'audience "interests" senza tag:
  // il parent deve inviarla davvero senza tag (niente fallback su descrizione o tag già salvati).
  confirmedWithoutTags?: boolean;
  previewData?: AudiencePreviewAccuracyView | null;
  fullRangePreviewData?: AudiencePreviewAccuracyView | null;
  audienceStatus?: AudienceFormStatus | null;
  isPublic?: boolean;
  // Volume effettivo estratto (metadato, non un form control): mostrato nell'Overview in edit.
  actualVolume?: number | null;
  // ID dell'audience (metadato), mostrato sotto il nome nell'Overview in edit.
  audienceId?: string | null;
}

interface PreviewBlockEntry {
  type: 'block' | 'operator';
  value: string;
  index?: number;
}

@Component({
  selector: 'app-audience-create',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, CustomSelectComponent, SearchClearDirective],
  templateUrl: './audience-create.component.html',
  styleUrl: './audience-create.component.scss'
})
export class AudienceCreateComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly PREVIEW_CHART_WIDTH = 520;
  private static readonly PREVIEW_CHART_HEIGHT = 260;
  private static readonly PREVIEW_CHART_LEFT = 40;
  private static readonly PREVIEW_CHART_TOP = 20;
  private static readonly PREVIEW_CHART_BOTTOM = 220;
  private static readonly PREVIEW_CHART_RIGHT = 24;
  private static readonly PREVIEW_MIN_DISTANCE = 0.1;

  @Input() ruleAudienceOptions: Array<{ id: string; name: string }> = [];
  @Input() customImportOptions: Array<{ id: string; name: string }> = [];
  @Input() sourceGroupOptions: Array<{ id: string; name: string; type?: string | null }> = [];
  @Input() optionsReady = false;
  @Input() skipDcrRuleFilters = false;
  @Input() submitting = false;
  @Input() mode: 'create' | 'edit' | 'clone' = 'create';
  @Input() initialValue: Partial<AudienceCreateFormValue> | null = null;
  @Input() isTNCDataEntity = false;
  @Input() isEntityTNCIDPlusMember = false;
  @Input() canMakePublic = false;
  @Input() previewing = false;
  @Input() ownerInfo: { id: string; name: string } | null = null;
  @Input() hasObsoleteFilters = false;
  // Raw country list (from the parent's single getCountries() fetch, shared instead of
  // re-fetched here) — the "All Locations" pseudo-option is added locally, see selectableCountryOptions.
  @Input() countryOptions: CountryOption[] = [];
  // Dimensione effettiva dell'audience (volume estratto), mostrata nell'Overview in edit.
  // Non è un @Input né un form control: arriva dentro initialValue e sopravvive al popstate.
  actualVolume: number | null = null;
  audienceId: string | null = null;
  audienceIdCopied = false;
  @Output() cancel = new EventEmitter<void>();
  @Output() save = new EventEmitter<AudienceCreateFormValue>();
  @Output() preview = new EventEmitter<AudienceCreateFormValue>();
  @Output() staleItemsDetected = new EventEmitter<void>();
  // Emesso quando l'admin cambia la modalità di espansione: il parent ricarica la curva della versione scelta.
  @Output() expandVersionChange = new EventEmitter<boolean>();

  readonly frequencies = ['Daily', 'Weekly', 'Monthly', 'Once'];
  readonly ageRanges = ['All Ages', '18-24', '25-34', '35-55', '45-54', '55-64', '65+', 'Unknown'];
  readonly genders = ['All Gender', 'Female', 'Male', 'Unknown'];
  readonly italyRegions = [
    'All Regions',
    'Abruzzo',
    'Basilicata',
    'Calabria',
    'Campania',
    'Emilia-Romagna',
    'Friuli-Venezia Giulia',
    'Lazio',
    'Liguria',
    'Lombardy',
    'Marche',
    'Molise',
    'Piedmont',
    'Apulia',
    'Sardinia',
    'Sicily',
    'Trentino-Alto Adige/Südtirol',
    'Tuscany',
    'Umbria',
    'Aosta Valley',
    'Veneto'
  ];
  readonly audienceTypes = [
    { value: AUDIENCE_TYPES.INTERESTS, labelKey: 'audiences.create.types.interest' },
    { value: AUDIENCE_TYPES.CUSTOM, labelKey: 'audiences.create.types.rule' }
  ];
  readonly adminAudienceTypes = [
    { value: AUDIENCE_TYPES.CUSTOM_QUERY, labelKey: 'audiences.create.types.customquery' }
  ];
  readonly ruleActions: { type: RuleGroupType; labelKey: string }[] = [
    { type: 'audience', labelKey: 'audiences.create.rule.actions.audience' },
    { type: 'goal', labelKey: 'audiences.create.rule.actions.goal' },
    { type: 'custom_import', labelKey: 'audiences.create.rule.actions.custom_import' },
    { type: 'source', labelKey: 'audiences.create.rule.actions.source' }
  ];
  readonly logicOperators: LogicalOperator[] = ['AND', 'OR', 'AND NOT'];
  readonly accuracyLevels: AudienceQuality[] = ['high', 'medium', 'low'];
  frequencyOptions: CustomSelectOption[] = this.toSelectOptions(this.frequencies);
  ageOptions: CustomSelectOption[] = [];
  genderOptions: CustomSelectOption[] = [];
  logicOperatorSelectOptions: CustomSelectOption[] = [];
  lookalikeMethodOptions: CustomSelectOption[] = [];
  readonly hasGlobalDataAccess: boolean;

  createForm: FormGroup;
  private subscriptions = new Subscription();
  private isInitializingForm = false;
  // Frequenza salvata sul DB dell'audience in edit/clone (null in create), base per syncFrequency
  private savedFrequency: string | null = null;
  private initialValueApplied = false;
  private staleItemsEmitted = false;
  private goalsLoaded = false;
  private currentAudienceStatus: AudienceFormStatus | null = null;
  analysisLoading = false;
  readonly defaultLocationCode = '__ALL__';
  private defaultLocationLabel = 'All Locations';
  private regionList: string[] = [...this.italyRegions];
  selectedCountryCode: string | null = null;
  private loadedRegionCountryCode: string | null = null;
  locationDropdownOpen = false;
  locationSearchTerm = '';
  regionDropdownOpen = false;
  goalOptions: Array<{ id: string; name: string; sourceGroupId?: string | null }> = [];
  previewData: AudiencePreviewAccuracyView | null = null;
  previewDirty = false;
  showFullCurve = false;
  private previewCompositionSignature: string | null = null;
  // Filtri che determinano la curva di un'audience "interests" (tag, età, genere, paese, regioni):
  // finché differiscono da quelli presenti all'apertura della modale la curva salvata non è più
  // valida e va nascosta; tornando ai filtri iniziali il grafico riappare identico.
  private initialInterestFiltersSignature: string | null = null;
  interestFiltersChanged = false;
  private normalPreviewData: AudiencePreviewAccuracyView | null = null;
  // true se la modalità di espansione selezionata (standard/pesata) non ha ancora una curva calcolata
  expandVersionCurveUnavailable = false;
  // Conferma "audience interests senza tag": l'azione (anteprima o salvataggio) resta in sospeso
  // finché l'utente non risponde.
  noInterestsConfirmOpen = false;
  private pendingNoInterestsAction: 'preview' | 'save' | null = null;
  private fullRangePreviewDataStore: AudiencePreviewAccuracyView | null = null;

  get hasFullRangeData(): boolean {
    return !!this.fullRangePreviewDataStore;
  }
  private initialRelevantCompositionSignature: string | null = null;

  constructor(
    private fb: FormBuilder,
    private audienceApi: AudienceApiService,
    private translate: TranslateService,
    private authService: AuthService,
    private notificationService: NotificationService
  ) {
    this.hasGlobalDataAccess = this.authService.hasGlobalDataAccess();
    this.rebuildSelectOptions();
    this.subscriptions.add(
      this.translate.onLangChange.subscribe(() => this.rebuildSelectOptions())
    );
    this.logicOperatorSelectOptions = this.buildOperatorOptions();
    this.defaultLocationLabel = this.translate.instant('audiences.create.filters.locationAll') || this.defaultLocationLabel;
    this.createForm = this.fb.group({
      type: [AUDIENCE_TYPES.INTERESTS, Validators.required],
      name: ['', Validators.required],
      description: [''],
      frequency: ['Daily', Validators.required],
      startDate: [this.getDefaultDate(), Validators.required],
      endDate: [''],
      ongoing: [true],
      aiDescription: [''],
      useAllNetworkData: [true],
      expandSimilarUsers: [true],
      expandVersionWeighted: [true],
      lookalikeMethod: ['knn' as LookalikeMethod],
      ageRange: [['All Ages']],
      gender: [['All Gender']],
      location: ['Italy'],
      regions: [['All Regions']],
      audienceQuality: ['medium'],
      extendVolumes: [null as number | null],
      previewAccuracyStep: [null as number | null],
      aiTags: [[]],
      isPublic: [false],
      ruleBlocks: this.fb.array([])
    });
    this.setupAudienceTypeListener();
    this.setupExpandSimilarUsersListener();
    this.setupExpandVersionListener();
    this.setupOngoingListener();
    this.setupLocationListener();
    this.setupAudienceSizeListener();
    this.setupAgeRangeListener();
    this.setupGenderListener();
    this.setupRegionsListener();
    this.setupPreviewInvalidation();
    this.addRuleBlock();
  }

  ngOnInit(): void {
    this.loadGoals();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialValue'] && changes['initialValue'].currentValue) {
      this.applyInitialValue(changes['initialValue'].currentValue as Partial<AudienceCreateFormValue>);
    } else if (changes['countryOptions'] && !changes['countryOptions'].firstChange) {
      // countryOptions arrived after the component mounted (e.g. new-audience flow, where the
      // parent's getCountries() fetch resolves after this component is created): re-resolve the
      // current location now that real data is available.
      this.applyLocationSelection(this.createForm.get('location')?.value || '');
    }
    if (changes['mode'] && changes['mode'].currentValue === 'create' && !this.initialValue) {
      this.resetForm();
    }
    if (changes['isTNCDataEntity'] || changes['isEntityTNCIDPlusMember']) {
      this.updateFirstPartyControlState();
    }
    if (changes['ruleAudienceOptions'] || changes['customImportOptions'] || changes['sourceGroupOptions']) {
      this.checkAndEmitStaleItems();
    }
    if (changes['optionsReady']?.currentValue === true) {
      this.checkAndEmitStaleItems();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  @HostListener('document:click')
  handleDocumentClick(): void {
    this.closeLocationDropdown();
    this.closeRegionDropdown();
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasPendingChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  get isInterestBased(): boolean {
    return this.createForm.get('type')?.value === AUDIENCE_TYPES.INTERESTS;
  }

  // Audience interests senza tag: non viene espansa (ExpandSimilarUsers = false), quindi non ha
  // curva di accuratezza né anteprima da calcolare.
  get isInterestBasedWithoutTags(): boolean {
    return this.isInterestBased && !((this.createForm.get('aiTags')?.value as string[] | null) ?? []).length;
  }

  get isRuleBased(): boolean {
    return this.createForm.get('type')?.value === AUDIENCE_TYPES.CUSTOM;
  }

  get isCustomQueryBased(): boolean {
    return this.createForm.get('type')?.value === AUDIENCE_TYPES.CUSTOM_QUERY;
  }

  get ruleBlocks(): FormArray<FormGroup> {
    return this.createForm.get('ruleBlocks') as FormArray<FormGroup>;
  }

  get rulePreviewBlocks(): string[] {
    const blocks = this.ruleBlocks.controls
      .map((block, index) => this.formatBlockPreview(block as FormGroup, index))
      .filter(block => !!block);
    if (blocks.length <= 1) {
      return blocks;
    }
    const result: string[] = [];
    blocks.forEach((block, index) => {
      if (!block) {
        return;
      }
      result.push(block);
      if (index < blocks.length - 1) {
        const operator = this.getBlockOperator(index + 1);
        result.push(operator);
      }
    });
    return result;
  }

  isOperatorBlock(value: string): boolean {
    const normalized = (value || '').toString().trim().toUpperCase();
    return normalized === 'AND' || normalized === 'OR' || normalized === 'AND NOT' || normalized === 'NOT';
  }

  getPreviewBlockIndex(index: number): number {
    const entries = this.rulePreviewBlocks.slice(0, index + 1);
    return entries.filter(entry => !this.isOperatorBlock(entry)).length;
  }

  get groupedRulePreviewBlocks(): PreviewBlockEntry[] {
    const blocks = this.rulePreviewBlocks;
    const result: PreviewBlockEntry[] = [];
    blocks.forEach((entry, idx) => {
      if (this.isOperatorBlock(entry)) {
        result.push({ type: 'operator', value: this.normalizePreviewOperator(entry) });
      } else {
        result.push({ type: 'block', value: entry, index: this.getPreviewBlockIndex(idx) });
      }
    });
    return result;
  }

  trackByPreviewBlock(index: number, entry: PreviewBlockEntry): string {
    return entry.type === 'operator'
      ? `op-${index}-${entry.value}`
      : `block-${entry.index}-${entry.value}`;
  }

  totalRuleGroups(): number {
    return this.ruleBlocks.controls.reduce((sum, block) => {
      const groups = (block.get('groups') as FormArray<FormGroup>) ?? this.fb.array([]);
      return sum + groups.length;
    }, 0);
  }

  blockGroups(blockIndex: number): FormArray<FormGroup> {
    const block = this.ruleBlocks.at(blockIndex);
    return (block?.get('groups') as FormArray<FormGroup>) ?? this.fb.array([]);
  }

  get aiTagsControl(): FormControl<string[]> {
    return this.createForm.get('aiTags') as FormControl<string[]>;
  }

  get extendVolumesControl(): FormControl<number | null> {
    return this.createForm.get('extendVolumes') as FormControl<number | null>;
  }

  get canEditInterestVolume(): boolean {
    return this.hasGlobalDataAccess;
  }

  get regionsControl(): FormControl<string[]> {
    return this.createForm.get('regions') as FormControl<string[]>;
  }

  get regionSelectionEnabled(): boolean {
    return this.selectedCountryCode === 'IT';
  }

  get expandSimilarUsers(): boolean {
    return !!this.createForm.get('expandSimilarUsers')?.value;
  }

  // La scelta della modalità di espansione (pesata IDF) è riservata agli utenti
  // con permesso admin_dashboard.all (o superiore, es. 'all'). Il backend rifiuta
  // comunque il valore 'weighted' per chi non è autorizzato.
  get canUseWeightedExpansion(): boolean {
    return this.authService.hasPermission(UserPermissions.ADMIN_DASHBOARD);
  }

  // Scelta del metodo del lookalike: stesso permesso, solo per le custom con espansione attiva.
  get canChooseLookalikeMethod(): boolean {
    return this.canUseWeightedExpansion && this.isRuleBased && this.expandSimilarUsers;
  }

  // Custom Query: in create l'espansione è forzata a "No" (Yes disabilitato);
  // in edit/clone si mostra il valore che arriva dal DB e si disabilita l'opposto.
  get isSimilarUsersYesDisabled(): boolean {
    if (!this.isCustomQueryBased) {
      return false;
    }
    if (this.mode === 'create') {
      return true;
    }
    return !this.expandSimilarUsers;
  }

  get isSimilarUsersNoDisabled(): boolean {
    if (!this.isCustomQueryBased) {
      return false;
    }
    if (this.mode === 'create') {
      return false;
    }
    return this.expandSimilarUsers;
  }

  get isExistingNonPreviewAudience(): boolean {
    return !!this.currentAudienceStatus && this.currentAudienceStatus !== 'draft';
  }

  get isPreviewInteractionDisabled(): boolean {
    return this.previewDirty;
  }

  get requiresPreviewFlow(): boolean {
    if (this.isExistingNonPreviewAudience) {
      return false;
    }
    return (this.isInterestBased && !this.isInterestBasedWithoutTags) || (this.isRuleBased && this.expandSimilarUsers);
  }

  get isPreviewReady(): boolean {
    return !!this.previewData && !this.previewDirty;
  }

  get shouldShowPreviewPendingWarning(): boolean {
    return this.mode === 'edit'
      && this.currentAudienceStatus === 'draft'
      && this.requiresPreviewFlow
      && !this.previewData;
  }

  get shouldUseLegacyAccuracySelection(): boolean {
    return this.isExistingNonPreviewAudience && !this.previewData;
  }

  get shouldShowAccuracyPreview(): boolean {
    // Curva non più rappresentativa dei filtri correnti: si nasconde finché non si torna
    // ai filtri iniziali (o finché non viene ricalcolata dopo il salvataggio).
    if (this.interestFiltersChanged) {
      return false;
    }
    if (this.isExistingNonPreviewAudience) {
      return !!this.previewData;
    }
    if (this.currentAudienceStatus === 'draft' && this.previewData) {
      return true;
    }
    return this.requiresPreviewFlow && this.isPreviewReady;
  }

  get shouldShowScheduleSection(): boolean {
    // Se l'audience esiste già, mostra sempre la schedulazione
    if (this.currentAudienceStatus) {
      return true;
    }
    return (this.isRuleBased && !this.expandSimilarUsers) || this.shouldShowAccuracyPreview || this.isCustomQueryBased;
  }

  get shouldShowSaveButton(): boolean {
    if (this.shouldShowPreviewPendingWarning) {
      return false;
    }
    if (this.shouldShowDraftButton) {
      return false;
    }
    return !this.requiresPreviewFlow || this.isPreviewReady;
  }

  get shouldShowPreviewButton(): boolean {
    if (this.shouldShowPreviewPendingWarning) {
      return false;
    }
    return (this.requiresPreviewFlow && !this.isPreviewReady) || this.shouldShowDraftButton;
  }

  get shouldShowDraftButton(): boolean {
    return this.mode === 'edit' && this.shouldShowAccuracyPreview && this.isPreviewInteractionDisabled;
  }

  get primaryPreviewButtonLabelKey(): string {
    return this.shouldShowDraftButton
      ? 'audiences.actions.createDraft'
      : 'audiences.actions.preview';
  }

  get primarySaveButtonLabelKey(): string {
    return 'audiences.actions.save';
  }

  get isAudienceTypeLocked(): boolean {
    return this.mode === 'edit' || this.mode === 'clone';
  }

  get previewSliderMax(): number {
    return Math.max((this.previewData?.points.length || 1) - 1, 0);
  }

  get previewSliderMin(): number {
    return 0;
  }

  get previewAccuracyStepControl(): FormControl<number | null> {
    return this.createForm.get('previewAccuracyStep') as FormControl<number | null>;
  }

  get selectedPreviewPoint(): AudiencePreviewPointView | null {
    if (!this.previewData?.points?.length) {
      return null;
    }
    const step = this.getCurrentPreviewStep() ?? 0;
    return this.previewData.points[step] ?? this.previewData.points[0] ?? null;
  }

  // Punto suggerito della curva (volume atteso alla soglia consigliata), indipendente dallo slider.
  get suggestedPreviewPoint(): AudiencePreviewPointView | null {
    if (!this.previewData?.points?.length) {
      return null;
    }
    const step = this.previewData.suggestedStep ?? this.previewData.defaultStep ?? 0;
    return this.previewData.points[step] ?? this.previewData.points[0] ?? null;
  }

  get previewChartPath(): string {
    const points = this.previewData?.points || [];
    if (points.length < 2) {
      return '';
    }
    const width = AudienceCreateComponent.PREVIEW_CHART_WIDTH;
    const left = AudienceCreateComponent.PREVIEW_CHART_LEFT;
    const top = AudienceCreateComponent.PREVIEW_CHART_TOP;
    const bottom = AudienceCreateComponent.PREVIEW_CHART_BOTTOM;
    const chartWidth = width - left - AudienceCreateComponent.PREVIEW_CHART_RIGHT;
    const chartHeight = bottom - top;
    const yRange = this.getPreviewYRange(points);

    return points.map((point, index) => {
      const x = left + ((chartWidth / Math.max(points.length - 1, 1)) * index);
      const y = bottom - (((point.users - yRange.base) / yRange.span) * chartHeight);
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    }).join(' ');
  }

  // Origine dell'asse Y. Per le custom con espansione (lookalike) la curva parte dal volume del seed,
  // che schiaccerebbe contro il bordo superiore la crescita dovuta ai nuovi utenti: l'asse parte dal
  // primo punto visibile (volume minimo mostrato). Per tutte le altre resta da 0.
  private getPreviewYRange(points: AudiencePreviewPointView[]): { base: number; span: number } {
    const maxUsers = Math.max(...points.map(point => point.users), 1);
    const minUsers = Math.min(...points.map(point => point.users));
    const startFromMin = this.isRuleBased && !!this.expandSimilarUsersControl?.value && minUsers < maxUsers;
    const base = startFromMin ? minUsers : 0;
    return { base, span: Math.max(maxUsers - base, 1) };
  }

  get previewChartAreaPath(): string {
    const points = this.previewData?.points || [];
    if (points.length < 2) {
      return '';
    }
    const width = AudienceCreateComponent.PREVIEW_CHART_WIDTH;
    const left = AudienceCreateComponent.PREVIEW_CHART_LEFT;
    const top = AudienceCreateComponent.PREVIEW_CHART_TOP;
    const bottom = AudienceCreateComponent.PREVIEW_CHART_BOTTOM;
    const chartWidth = width - left - AudienceCreateComponent.PREVIEW_CHART_RIGHT;
    const chartHeight = bottom - top;
    const yRange = this.getPreviewYRange(points);

    // Start at bottom-left
    const firstX = left;
    let path = `M ${firstX} ${bottom}`;

    // Draw line through all points
    points.forEach((point, index) => {
      const x = left + ((chartWidth / Math.max(points.length - 1, 1)) * index);
      const y = bottom - (((point.users - yRange.base) / yRange.span) * chartHeight);
      path += ` L ${x.toFixed(2)} ${y.toFixed(2)}`;
    });

    // Close path at bottom-right
    const lastX = left + chartWidth;
    path += ` L ${lastX} ${bottom} Z`;

    return path;
  }

  get previewYAxisTicks(): Array<{ y: number; value: string }> {
    const points = this.previewData?.points || [];
    if (!points.length) {
      return [];
    }
    const yRange = this.getPreviewYRange(points);

    // Generate 10 ticks (0%, 10%, 20%, ... 100%) for better granularity
    const tickCount = 5;
    return Array.from({ length: tickCount + 1 }, (_, i) => {
      const ratio = i / tickCount;
      const value = Math.round(yRange.base + (yRange.span * ratio));
      return {
        y: AudienceCreateComponent.PREVIEW_CHART_BOTTOM - (ratio * (AudienceCreateComponent.PREVIEW_CHART_BOTTOM - AudienceCreateComponent.PREVIEW_CHART_TOP)),
        value: this.formatCompactNumber(value)
      };
    });
  }

  // X-axis labels removed - chart shows only Y-axis with numbers

  private formatCompactNumber(value: number): string {
    if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(1)}M`;
    }
    if (value >= 1_000) {
      return `${(value / 1_000).toFixed(1)}K`;
    }
    return `${value}`;
  }

  get expandSimilarUsersControl(): FormControl<boolean> {
    return this.createForm.get('expandSimilarUsers') as FormControl<boolean>;
  }

  get selectedPreviewPointPosition(): { x: number; y: number } | null {
    return this.getPreviewPointPosition(this.getCurrentPreviewStep());
  }

  get suggestedPreviewPointPosition(): { x: number; y: number } | null {
    return this.getPreviewPointPosition(this.previewData?.suggestedStep ?? null);
  }

  private getPreviewPointPosition(stepValue: number | null | undefined): { x: number; y: number } | null {
    const points = this.previewData?.points || [];
    if (!points.length || stepValue === null || stepValue === undefined) {
      return null;
    }
    const step = Number(stepValue);
    if (step < 0 || step >= points.length) {
      return null;
    }
    const point = points[step];
    const width = AudienceCreateComponent.PREVIEW_CHART_WIDTH;
    const left = AudienceCreateComponent.PREVIEW_CHART_LEFT;
    const top = AudienceCreateComponent.PREVIEW_CHART_TOP;
    const bottom = AudienceCreateComponent.PREVIEW_CHART_BOTTOM;
    const chartWidth = width - left - AudienceCreateComponent.PREVIEW_CHART_RIGHT;
    const chartHeight = bottom - top;
    const yRange = this.getPreviewYRange(points);

    const x = left + ((chartWidth / Math.max(points.length - 1, 1)) * step);
    const y = bottom - (((point.users - yRange.base) / yRange.span) * chartHeight);

    return { x, y };
  }

  get regionOptions(): CustomSelectOption[] {
    return this.regionList.map(region => ({
      value: region,
      label: region === 'All Regions'
        ? this.translate.instant('audiences.create.filters.regionAll')
        : region
    }));
  }

  // Prepends the local "All Locations" pseudo-option to the raw list received from the parent.
  get selectableCountryOptions(): CountryOption[] {
    return [{ code: this.defaultLocationCode, name: this.defaultLocationLabel }, ...(this.countryOptions ?? [])];
  }

  get filteredCountryOptions(): CountryOption[] {
    const term = this.locationSearchTerm.trim().toLowerCase();
    if (!term.length) {
      return this.selectableCountryOptions;
    }
    return this.selectableCountryOptions.filter(option => option.name.toLowerCase().includes(term));
  }

  get currentLocationLabel(): string {
    return (this.createForm.get('location')?.value || this.defaultLocationLabel).toString();
  }

  get regionSummary(): string {
    const regions = this.regionsControl.value || [];
    if (!this.regionSelectionEnabled) {
      return this.translate.instant('audiences.create.fields.regionDisabled');
    }
    if (!regions.length) {
      return this.translate.instant('audiences.create.fields.regionPlaceholder');
    }
    if (regions.length === 1) {
      return regions[0] === 'All Regions'
        ? this.translate.instant('audiences.create.filters.regionAll')
        : regions[0];
    }
    return this.translate.instant('audiences.create.fields.regionCount', { count: regions.length });
  }

  addRuleBlock(): void {
    this.ruleBlocks.push(this.createRuleBlock('AND'));
  }

  removeRuleBlock(index: number): void {
    this.ruleBlocks.removeAt(index);
    if (!this.ruleBlocks.length) {
      this.addRuleBlock();
    }
  }

  addRuleGroup(blockIndex: number, type: RuleGroupType): void {
    const groups = this.blockGroups(blockIndex);
    groups.push(this.fb.group({
      type: [type],
      valueOperator: ['AND'],
      payload: [[]]
    }));
  }

  removeRuleGroup(blockIndex: number, groupIndex: number): void {
    const groups = this.blockGroups(blockIndex);
    groups.removeAt(groupIndex);
    if (!groups.length) {
      this.ruleBlocks.removeAt(blockIndex);
      if (!this.ruleBlocks.length) {
        this.addRuleBlock();
      }
    }
  }

  handleCancel(): void {
    // Don't reset form - keep data in case user wants to go back
    this.cancel.emit();
  }

  onPreviewRequest(): void {
    const submission = this.buildSubmissionPayload('preview');
    if (!submission) {
      return;
    }
    this.preview.emit(submission);
  }

  onSubmit(): void {
    const submission = this.buildSubmissionPayload('save');
    if (!submission) {
      return;
    }
    this.save.emit(submission);
    // Don't reset form here - let the parent component handle it after successful save
  }

  confirmNoInterests(): void {
    const action = this.pendingNoInterestsAction;
    this.noInterestsConfirmOpen = false;
    this.pendingNoInterestsAction = null;
    if (!action) {
      return;
    }
    const submission = this.buildSubmissionPayload(action, true);
    if (!submission) {
      return;
    }
    // Senza tag l'audience non viene espansa e non c'è curva da mostrare: anche da "Anteprima"
    // si procede direttamente al salvataggio.
    this.save.emit(submission);
  }

  cancelNoInterests(): void {
    this.noInterestsConfirmOpen = false;
    this.pendingNoInterestsAction = null;
  }

  onPreviewSliderChange(step: string | number): void {
    if (this.isPreviewInteractionDisabled) {
      return;
    }
    const numeric = Math.min(Math.max(Number(step) || 0, this.previewSliderMin), this.previewSliderMax);
    this.previewAccuracyStepControl.setValue(numeric);
    if (this.previewData) {
      this.previewData = {
        ...this.previewData,
        selectedStep: numeric
      };
    }
  }

  onChartClick(event: MouseEvent): void {
    if (this.isPreviewInteractionDisabled) {
      return;
    }
    const points = this.previewData?.points || [];
    if (points.length < 2) {
      return;
    }

    // Get SVG element and its bounding box
    const svgElement = (event.currentTarget as HTMLElement).querySelector('svg');
    if (!svgElement) {
      return;
    }

    const rect = svgElement.getBoundingClientRect();
    const clickX = event.clientX - rect.left;

    // Convert click position to SVG viewBox coordinates
    const viewBoxWidth = AudienceCreateComponent.PREVIEW_CHART_WIDTH;
    const svgX = (clickX / rect.width) * viewBoxWidth;

    // Calculate which point is closest
    const width = AudienceCreateComponent.PREVIEW_CHART_WIDTH;
    const left = AudienceCreateComponent.PREVIEW_CHART_LEFT;
    const chartWidth = width - left - AudienceCreateComponent.PREVIEW_CHART_RIGHT;

    // Find the closest point index
    let closestIndex = 0;
    let closestDistance = Infinity;

    points.forEach((point, index) => {
      const pointX = left + ((chartWidth / Math.max(points.length - 1, 1)) * index);
      const distance = Math.abs(svgX - pointX);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    // Update the slider to this point
    this.onPreviewSliderChange(Math.max(closestIndex, this.previewSliderMin));
  }

  movePreviewSlider(delta: number): void {
    if (this.isPreviewInteractionDisabled || !delta) {
      return;
    }
    const current = this.getCurrentPreviewStep() ?? this.previewSliderMin;
    this.onPreviewSliderChange(current + delta);
  }

  toggleFullCurve(): void {
    // Preserve the selected distance before switching point sets
    const currentStep = this.previewAccuracyStepControl.value;
    const currentDistance = (currentStep !== null && currentStep !== undefined && this.previewData?.points)
      ? (this.previewData.points[currentStep]?.minDistance ?? null)
      : null;

    this.showFullCurve = !this.showFullCurve;
    // Clear the step so applyPreviewState does not try to remap an index from the old set
    this.previewAccuracyStepControl.setValue(null, { emitEvent: false });
    const source = this.showFullCurve ? this.fullRangePreviewDataStore : this.normalPreviewData;
    this.applyPreviewState(source);

    // Restore the slider to the closest point matching the original distance
    if (currentDistance !== null && this.previewData?.points.length) {
      let closestStep = 0;
      let closestDiff = Infinity;
      this.previewData.points.forEach((point, i) => {
        const diff = Math.abs(point.minDistance - currentDistance);
        if (diff < closestDiff) { closestDiff = diff; closestStep = i; }
      });
      this.onPreviewSliderChange(closestStep);
    }
  }

  resetPreviewSliderToSuggested(): void {
    if (this.isPreviewInteractionDisabled) {
      return;
    }
    const suggested = this.previewData?.suggestedStep;
    if (suggested === null || suggested === undefined) {
      return;
    }
    this.onPreviewSliderChange(suggested);
  }

  onAudienceTypeChange(type: AudienceType): void {
    if (this.isAudienceTypeLocked) {
      return;
    }
    this.createForm.get('type')?.setValue(type);
  }

  onExtendVolumesChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.value === '') {
      this.extendVolumesControl.setValue(null);
      this.extendVolumesControl.markAsDirty();
    }
  }

  onExtendVolumesInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Remove all non-digit characters
    const numericValue = value.replace(/\D/g, '');

    if (numericValue === '') {
      this.extendVolumesControl.setValue(null, { emitEvent: false });
      input.value = '';
      return;
    }

    // Update the form control with the numeric value
    this.extendVolumesControl.setValue(parseInt(numericValue, 10), { emitEvent: false });

    // Format the input value with thousands separator
    input.value = this.formatNumberWithSeparator(parseInt(numericValue, 10));
  }

  private buildSubmissionPayload(mode: 'preview' | 'save', allowEmptyTags = false): AudienceCreateFormValue | null {
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      this.scrollToFirstError();
      return null;
    }

    // Attenzione: quando la curva è nascosta solo perché i filtri interests sono cambiati, il
    // salvataggio deve restare quello normale (tag inclusi) — la curva verrà ricalcolata a valle.
    const confirmedWithoutTags = allowEmptyTags && this.isInterestBasedWithoutTags;
    const skipQualityValidation = (this.isRuleBased && !this.expandSimilarUsers)
      || confirmedWithoutTags
      || (this.isExistingNonPreviewAudience && !this.shouldShowAccuracyPreview && !this.interestFiltersChanged);
    if (skipQualityValidation) {
      // I tag sono la composizione stessa di un'audience interests: non vanno mai azzerati qui
      // (es. audience esistente senza curva per la modalità di espansione appena scelta).
      this.createForm.patchValue({
        audienceQuality: null,
        extendVolumes: null,
        ...(this.isInterestBased ? {} : { aiTags: [] })
      });
    }

    const formValue = this.createForm.getRawValue() as AudienceCreateFormValue;

    if (!this.validateCompositionRules(formValue, mode, allowEmptyTags)) {
      return null;
    }

    if (mode === 'save' && this.requiresPreviewFlow && !this.isPreviewReady) {
      this.notificationService.notify('error', this.translate.instant('audiences.create.errors.previewRequired'));
      return null;
    }

    // Filtri interests cambiati: l'accuratezza non è più selezionabile (curva nascosta e in
    // ricalcolo), quindi non va richiesta come obbligatoria al salvataggio.
    if (mode === 'save' && !skipQualityValidation && !this.requiresPreviewFlow && !this.interestFiltersChanged) {
      const hasGraphSelection = this.shouldShowAccuracyPreview && this.getCurrentPreviewStep() !== null;
      const accuracyControl = this.createForm.get('audienceQuality');

      if (!hasAccuracySelection(formValue, hasGraphSelection)) {
        if (accuracyControl) {
          accuracyControl.setErrors({ audienceQualityRequired: true });
          accuracyControl.markAsTouched();
        }
        this.scrollToFirstError();
        return null;
      }

      if (accuracyControl?.hasError('audienceQualityRequired')) {
        accuracyControl.setErrors(null);
      }
    }

    const regions = normalizeSubmissionRegions(this.selectedCountryCode, formValue.regions);

    return {
      ...formValue,
      regions,
      previewAccuracyStep: mode === 'save' && !this.interestFiltersChanged ? this.getCurrentPreviewStep() : null,
      interestFiltersChanged: this.interestFiltersChanged,
      confirmedWithoutTags,
      previewData: this.previewData,
      audienceStatus: (this.initialValue?.audienceStatus as AudienceFormStatus | null) ?? null
    };
  }

  private validateCompositionRules(formValue: AudienceCreateFormValue, mode: 'preview' | 'save', allowEmptyTags: boolean): boolean {
    const error = findCompositionError(formValue, {
      isCustomQuery: this.isCustomQueryBased,
      isRuleBased: this.isRuleBased,
      allLocationsLabel: this.defaultLocationLabel
    });
    if (!error) {
      return true;
    }

    // Audience interests senza tag: consentita, ma solo dopo una conferma esplicita (a ogni click).
    if (error === 'tagsRequired') {
      if (allowEmptyTags) {
        return true;
      }
      this.pendingNoInterestsAction = mode;
      this.noInterestsConfirmOpen = true;
      return false;
    }

    if (error === 'interestFilterRequired') {
      this.notificationService.notify('error', this.translate.instant('audiences.create.errors.interestFilterRequired'));
      this.scrollToFirstError();
      return false;
    }

    if (this.ruleBlocks.length > 0) {
      const firstBlock = this.ruleBlocks.at(0);
      firstBlock.setErrors({ ruleFilterRequired: true });
      firstBlock.markAsTouched();
    }
    this.notificationService.notify('error', this.translate.instant('audiences.create.errors.ruleFilterRequired'));
    this.scrollToFirstError();
    return false;
  }

  formatNumberWithSeparator(value: number | null): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '';
    }
    return value.toLocaleString('en-US');
  }

  getFormattedExtendVolumes(): string {
    const value = this.extendVolumesControl.value;
    return this.formatNumberWithSeparator(value);
  }

  private scrollToFirstError(): void {
    setTimeout(() => {
      const firstInvalidControl = document.querySelector('.form-field.has-error, input.ng-invalid, select.ng-invalid, textarea.ng-invalid');
      if (firstInvalidControl) {
        firstInvalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.createForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string | null {
    const field = this.createForm.get(fieldName);
    if (!field || !this.isFieldInvalid(fieldName)) {
      return null;
    }
    if (field.errors?.['required']) {
      return this.translate.instant('validation.required');
    }
    if (field.errors?.['audienceQualityRequired']) {
      return this.translate.instant('validation.audienceQualityRequired');
    }
    if (field.errors?.['min']) {
      return this.translate.instant('validation.min', { min: field.errors['min'].min });
    }
    if (field.errors?.['max']) {
      return this.translate.instant('validation.max', { max: field.errors['max'].max });
    }
    return this.translate.instant('validation.invalid');
  }

  async runAnalysis(): Promise<void> {
    const description = (this.createForm.get('aiDescription')?.value ?? '').toString().trim();
    if (!description) {
      return;
    }
    this.analysisLoading = true;
    try {
      const tags = await firstValueFrom(this.audienceApi.extractTags(description));
      this.aiTagsControl.setValue(tags ?? []);
    } catch (error) {
      console.error('[audience-create] analysis failed', error);
      this.aiTagsControl.setValue([]);
    } finally {
      this.analysisLoading = false;
    }
  }

  hasPendingChanges(): boolean {
    return this.createForm?.dirty ?? false;
  }

  addAiTag(tag: string): void {
    const trimmed = (tag ?? '').trim();
    if (!trimmed) {
      return;
    }
    const current = [...(this.aiTagsControl.value ?? [])];
    if (current.includes(trimmed)) {
      return;
    }
    current.push(trimmed);
    this.aiTagsControl.setValue(current);
  }

  setLocationToAll(): void {
    const fallback = { code: this.defaultLocationCode, name: this.defaultLocationLabel };
    this.selectCountry(fallback);
  }

  toggleLocationDropdown(event: Event): void {
    event.stopPropagation();
    this.locationDropdownOpen = !this.locationDropdownOpen;
    if (this.locationDropdownOpen) {
      this.locationSearchTerm = '';
    }
  }

  onLocationSearch(value: string): void {
    this.locationSearchTerm = (value || '').toString();
  }

  selectCountry(option: CountryOption, event?: Event): void {
    event?.stopPropagation();
    if (!option) {
      return;
    }
    this.createForm.get('location')?.setValue(option.name);
    this.applyLocationSelection(option.name);
    this.closeLocationDropdown();
  }

  closeLocationDropdown(): void {
    if (!this.locationDropdownOpen) {
      return;
    }
    this.locationDropdownOpen = false;
    this.locationSearchTerm = '';
  }

  isRegionSelected(region: string): boolean {
    return (this.regionsControl.value || []).includes(region);
  }

  toggleRegion(region: string): void {
    if (!this.regionSelectionEnabled) {
      return;
    }
    const current = new Set(this.regionsControl.value || []);
    if (current.has(region)) {
      current.delete(region);
    } else {
      current.add(region);
    }
    this.regionsControl.setValue(Array.from(current));
  }

  selectAllRegions(): void {
    if (!this.regionSelectionEnabled || !this.regionList.length) {
      return;
    }
    this.regionsControl.setValue([...this.regionList]);
  }

  toggleRegionDropdown(event: Event): void {
    event.stopPropagation();
    if (!this.regionSelectionEnabled) {
      return;
    }
    this.regionDropdownOpen = !this.regionDropdownOpen;
  }

  closeRegionDropdown(): void {
    if (!this.regionDropdownOpen) {
      return;
    }
    this.regionDropdownOpen = false;
  }

  isPayloadSelected(blockIndex: number, groupIndex: number, optionId: string | number | null | undefined): boolean {
    const groups = this.blockGroups(blockIndex);
    const control = groups.at(groupIndex)?.get('payload');
    if (!control) {
      return false;
    }
    const normalized = this.normalizeGroupPayload(control.value);
    const target = optionId?.toString();
    if (!target) {
      return false;
    }
    return normalized.includes(target);
  }

  togglePayloadOption(
    blockIndex: number,
    groupIndex: number,
    optionId: string | number | null | undefined,
    checked: boolean
  ): void {
    const groups = this.blockGroups(blockIndex);
    const control = groups.at(groupIndex)?.get('payload');
    if (!control) {
      return;
    }
    const target = optionId?.toString();
    if (!target) {
      return;
    }
    const next = this.normalizeGroupPayload(control.value);
    const exists = next.includes(target);
    if (checked && !exists) {
      next.push(target);
    } else if (!checked && exists) {
      const index = next.indexOf(target);
      if (index >= 0) {
        next.splice(index, 1);
      }
    }

    // Keep preview invalidation aligned with checkbox changes in open rule groups.
    control.markAsDirty();
    control.setValue(next, { emitEvent: false });
    control.markAsDirty();
    control.updateValueAndValidity({ emitEvent: false });
    this.syncPreviewDirtyState();
  }

  filterOptions<T extends { name?: string | null }>(
    options: T[] | null | undefined,
    term?: string | null
  ): T[] {
    const list = Array.isArray(options) ? options : [];
    const normalized = (term || '').toString().trim().toLowerCase();
    if (!normalized) {
      return list;
    }
    return list.filter(option => (option?.name || '').toString().toLowerCase().includes(normalized));
  }

  private getBlockOperator(blockIndex: number): string {
    const block = this.ruleBlocks.at(blockIndex);
    const value = (block?.get('operator')?.value || 'AND').toString();
    return this.getOperatorLabel(value);
  }

  private formatBlockPreview(block: FormGroup, blockIndex: number): string {
    const groups = block.get('groups') as FormArray<FormGroup>;
    if (!groups || !groups.length) {
      return '';
    }
    const segments: string[] = [];
    const totalGroups = groups.controls.length;
    groups.controls.forEach((group, groupIndex) => {
      const label = this.formatGroupLabel(group as FormGroup, totalGroups > 1);
      if (!label) {
        return;
      }
      if (groupIndex === 0) {
        segments.push(label);
      } else {
        const operator = (group.get('valueOperator')?.value || 'AND').toString();
        segments.push(`${this.getOperatorLabel(operator)} ${label}`);
      }
    });
    const joined = segments.join(' ');
    return joined ? `(${joined})` : '';
  }

  private formatGroupLabel(group: FormGroup, wrapForOperator: boolean): string {
    const type = (group.get('type')?.value || '').toString() as RuleGroupType;
    const payload = this.normalizeGroupPayload(group.get('payload')?.value);
    if (!type || !payload.length) {
      return '';
    }
    const labels = payload.map(value => this.resolveGroupOptionLabel(type, value)).filter(Boolean);
    if (!labels.length) {
      return '';
    }
    const connector = this.translate.instant('audiences.create.rule.preview.itemConnector');
    const expression = labels.join(` ${connector} `);
    const needsParens = wrapForOperator && labels.length > 1;
    return needsParens ? `(${expression})` : expression;
  }

  private resolveGroupOptionLabel(type: RuleGroupType, id: string): string {
    if (!id) {
      return '';
    }
    switch (type) {
      case 'custom_import':
        return this.customImportOptions.find(option => String(option.id) === id)?.name || id;
      case 'goal':
        return this.goalOptions.find(option => String(option.id) === id)?.name || id;
      case 'audience':
        return this.ruleAudienceOptions.find(option => String(option.id) === id)?.name || id;
      case 'source':
        return this.sourceGroupOptions.find(option => String(option.id) === id)?.name || id;
      default:
        return id;
    }
  }

  removeAiTag(index: number): void {
    const current = [...(this.aiTagsControl.value ?? [])];
    if (index < 0 || index >= current.length) {
      return;
    }
    current.splice(index, 1);
    this.aiTagsControl.setValue(current);
  }

  getSelectedPayloadTags(blockIndex: number, groupIndex: number): Array<{ id: string; name: string }> {
    const groups = this.blockGroups(blockIndex);
    const group = groups.at(groupIndex);
    if (!group) {
      return [];
    }
    const type = (group.get('type')?.value || '').toString() as RuleGroupType;
    const payload = this.normalizeGroupPayload(group.get('payload')?.value);
    if (!type || !payload.length) {
      return [];
    }
    return payload.map(id => ({
      id,
      name: this.resolveGroupOptionLabel(type, id)
    })).filter(item => item.name);
  }

  getStalePayloadItems(blockIndex: number, groupIndex: number): string[] {
    const groups = this.blockGroups(blockIndex);
    const group = groups.at(groupIndex);
    if (!group) return [];
    const type = (group.get('type')?.value || '').toString() as RuleGroupType;
    const payload = this.normalizeGroupPayload(group.get('payload')?.value);
    if (!type || !payload.length) return [];
    return payload.filter(id => !this.resolveGroupOptionExists(type, id));
  }

  private resolveGroupOptionExists(type: RuleGroupType, id: string): boolean {
    switch (type) {
      case 'custom_import': return !!this.customImportOptions.find(o => String(o.id) === id);
      case 'goal': return !!this.goalOptions.find(o => String(o.id) === id);
      case 'audience': return !!this.ruleAudienceOptions.find(o => String(o.id) === id);
      case 'source': return !!this.sourceGroupOptions.find(o => String(o.id) === id);
      default: return true;
    }
  }

  removePayloadTag(blockIndex: number, groupIndex: number, idToRemove: string): void {
    this.togglePayloadOption(blockIndex, groupIndex, idToRemove, false);
  }

  private checkAndEmitStaleItems(): void {
    if (!this.initialValueApplied) return;
    if (!this.optionsReady) return;
    if (!this.goalsLoaded) return;
    if (this.staleItemsEmitted) return;
    const blocks = this.ruleBlocks.controls as FormGroup[];
    const hasStale = blocks.some((block, bi) => {
      const groups = (block.get('groups') as FormArray)?.controls ?? [];
      return groups.some((_, gi) => this.getStalePayloadItems(bi, gi).length > 0);
    });
    if (hasStale) {
      this.staleItemsEmitted = true;
      this.staleItemsDetected.emit();
    }
  }

  trackRuleBlock(index: number): number {
    return index;
  }

  trackRuleGroup(index: number, _group: FormGroup): number {
    return index;
  }

  private setupExpandSimilarUsersListener(): void {
    const control = this.createForm.get('expandSimilarUsers');
    if (!control) {
      return;
    }
    this.subscriptions.add(
      control.valueChanges.subscribe(value => {
        if (!value) {
          this.extendVolumesControl.setValue(null, { emitEvent: false });
          const audienceQualityControl = this.createForm.get('audienceQuality');
          if (audienceQualityControl) {
            audienceQualityControl.setValue('medium', { emitEvent: false });
          }
        }
        if (!this.isInitializingForm) {
          this.syncFrequency();
        }
      })
    );
  }

  // Frequenze selezionabili, in ordine crescente (this.frequencies è già ordinato
  // Daily < Weekly < Monthly < Once): custom con espansione esclude Daily.
  private get availableFrequencies(): string[] {
    // rebuildSelectOptions gira nel costruttore prima che createForm esista
    return this.createForm && this.isRuleBased && this.expandSimilarUsers
      ? this.frequencies.filter(f => f !== 'Daily')
      : [...this.frequencies];
  }

  // Aggiorna le opzioni della select e il valore preselezionato:
  // - create: la minima tra le disponibili;
  // - edit/clone: la maggiore tra la minima disponibile e quella salvata sul DB.
  // L'utente resta libero di cambiarla nella select; si ricalcola solo al cambio di tipo/espansione.
  private syncFrequency(): void {
    const available = this.availableFrequencies;
    this.frequencyOptions = this.toSelectOptions(available);
    const frequencyControl = this.createForm.get('frequency');
    if (!frequencyControl) {
      return;
    }
    const minimum = available[0];
    const savedRank = this.savedFrequency ? this.frequencies.indexOf(this.savedFrequency) : -1;
    const next = savedRank > this.frequencies.indexOf(minimum) ? this.savedFrequency! : minimum;
    if (frequencyControl.value !== next) {
      frequencyControl.setValue(next, { emitEvent: false });
    }
  }

  private setupExpandVersionListener(): void {
    const control = this.createForm.get('expandVersionWeighted');
    if (!control) {
      return;
    }
    this.subscriptions.add(
      control.valueChanges.subscribe(value => {
        // Non emettere durante il patch iniziale del form (edit/clone)
        if (this.isInitializingForm) {
          return;
        }
        this.expandVersionChange.emit(!!value);
      })
    );
  }

  // Aggiorna solo la curva mostrata (senza toccare i campi del form) quando il parent ricarica
  // la preview per la modalità di espansione scelta. Azzera lo step così la maniglia si riposiziona
  // sul punto suggerito della nuova curva.
  applyExternalPreview(view: AudiencePreviewAccuracyView | null, fullRangeView: AudiencePreviewAccuracyView | null = null): void {
    this.showFullCurve = false;
    this.expandVersionCurveUnavailable = !view?.points?.length;
    this.normalPreviewData = view;
    this.fullRangePreviewDataStore = fullRangeView;
    this.previewAccuracyStepControl.setValue(null, { emitEvent: false });
    this.applyPreviewState(view);
  }

  copyAudienceId(): void {
    if (!this.audienceId) {
      return;
    }
    const text = this.audienceId;
    const copyPromise = (typeof navigator !== 'undefined' && navigator.clipboard?.writeText)
      ? navigator.clipboard.writeText(text)
      : this.fallbackCopyText(text);
    copyPromise.then(() => {
      this.audienceIdCopied = true;
      this.notificationService.notify('success', this.translate.instant('audiences.details.copySuccess'));
      setTimeout(() => { this.audienceIdCopied = false; }, 2000);
    }).catch(() => {
      this.notificationService.notify('error', this.translate.instant('audiences.details.copyError'));
    });
  }

  private fallbackCopyText(text: string): Promise<void> {
    if (typeof document === 'undefined') {
      return Promise.reject(new Error('Unavailable'));
    }
    return new Promise((resolve, reject) => {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (successful) {
          resolve();
        } else {
          reject(new Error('Copy failed'));
        }
      } catch (error) {
        reject(error instanceof Error ? error : new Error('Copy failed'));
      }
    });
  }

  private setupAudienceTypeListener(): void {
    const control = this.createForm.get('type');
    if (!control) {
      return;
    }
    this.subscriptions.add(
      control.valueChanges.subscribe(() => {
        // Clear validation errors when switching audience type
        const aiTagsControl = this.createForm.get('aiTags');
        if (aiTagsControl) {
          aiTagsControl.setErrors(null);
          aiTagsControl.markAsUntouched();
        }

        // Clear rule blocks errors
        if (this.ruleBlocks.length > 0) {
          this.ruleBlocks.controls.forEach(block => {
            block.setErrors(null);
            block.markAsUntouched();
          });
        }

        const similarControl = this.expandSimilarUsersControl;
        if (this.isRuleBased || this.isCustomQueryBased) {
          if (similarControl && similarControl.value) {
            similarControl.setValue(false);
          }
        } else {
          if (similarControl && !similarControl.value) {
            similarControl.setValue(true);
          }
          this.updateFirstPartyControlState();
        }
        if (!this.isInitializingForm) {
          this.syncFrequency();
        }
      })
    );
  }

  private updateFirstPartyControlState(): void {
    if ((this.isTNCDataEntity || !this.isEntityTNCIDPlusMember) && this.isInterestBased) {
      this.createForm.get('useAllNetworkData')?.setValue(true, { emitEvent: false });
    }
  }

  private setupLocationListener(): void {
    const control = this.createForm.get('location');
    if (!control) {
      return;
    }
    this.subscriptions.add(
      control.valueChanges.subscribe(value => {
        this.applyLocationSelection((value || '').toString());
      })
    );
  }

  private normalizeGroupPayload(payload: unknown): string[] {
    const values = Array.isArray(payload)
      ? payload
      : (payload === null || payload === undefined || payload === '')
        ? []
        : [payload];
    return values
      .map(value => value?.toString().trim() ?? '')
      .filter(value => value.length);
  }

  private createRuleBlock(operator: LogicalOperator): FormGroup {
    return this.fb.group({
      operator: [operator],
      groups: this.fb.array([])
    });
  }

  private normalizeLegacyRuleGroups(value: Partial<AudienceCreateFormValue>): AudienceCreateFormValue['ruleBlocks'] {
    const legacyGroups = (value as any)?.ruleGroups;
    if (!Array.isArray(legacyGroups) || !legacyGroups.length) {
      return [];
    }
    return [
      {
        operator: 'AND',
        groups: legacyGroups.map(group => ({
          type: (group?.type || 'custom_import') as RuleGroupType,
          valueOperator: (group?.valueOperator || group?.groupOperator || 'AND') as LogicalOperator,
          payload: this.normalizeGroupPayload(group?.payload)
        }))
      }
    ];
  }

  private setupOngoingListener(): void {
    const ongoingControl = this.createForm.get('ongoing');
    if (!ongoingControl) {
      return;
    }

    this.updateEndDateControlState(!!ongoingControl.value);
    this.subscriptions.add(
      ongoingControl.valueChanges.subscribe(value => this.updateEndDateControlState(!!value))
    );
  }

  private updateEndDateControlState(ongoing: boolean): void {
    const endDateControl = this.createForm.get('endDate');
    if (!endDateControl) {
      return;
    }
    if (ongoing) {
      endDateControl.disable({ emitEvent: false });
      endDateControl.clearValidators();
      endDateControl.setValue('', { emitEvent: false });
    } else {
      endDateControl.enable({ emitEvent: false });
      endDateControl.setValidators([Validators.required]);
    }
    endDateControl.updateValueAndValidity({ emitEvent: false });
  }

  private setupAudienceSizeListener(): void {
    const extendVolumesControl = this.createForm.get('extendVolumes');
    const audienceQualityControl = this.createForm.get('audienceQuality');

    if (!extendVolumesControl || !audienceQualityControl) {
      return;
    }

    // When extendVolumes is filled, clear audienceQuality
    this.subscriptions.add(
      extendVolumesControl.valueChanges.subscribe(value => {
        if (value !== null && value !== '' && value > 0) {
          audienceQualityControl.setValue(null, { emitEvent: false });
        }
      })
    );

    // When audienceQuality is selected, clear extendVolumes
    this.subscriptions.add(
      audienceQualityControl.valueChanges.subscribe(value => {
        if (value !== null && value !== '') {
          extendVolumesControl.setValue(null, { emitEvent: false });
        }
      })
    );
  }

  private setupAgeRangeListener(): void {
    const ageRangeControl = this.createForm.get('ageRange');
    if (!ageRangeControl) {
      return;
    }

    let previousValue = ageRangeControl.value || ['All Ages'];

    this.subscriptions.add(
      ageRangeControl.valueChanges.subscribe(value => {
        if (!Array.isArray(value)) {
          return;
        }

        // Prevent deselecting the last option - must always have at least one selected
        if (value.length === 0) {
          ageRangeControl.setValue(previousValue, { emitEvent: false });
          return;
        }

        // If "All Ages" is selected along with other options
        if (value.includes('All Ages') && value.length > 1) {
          // Check if "All Ages" was just added (last in array) or was already there
          const lastAdded = value[value.length - 1];
          if (lastAdded === 'All Ages') {
            // "All Ages" was just selected, remove all other age filters
            ageRangeControl.setValue(['All Ages'], { emitEvent: false });
            previousValue = ['All Ages'];
            this.syncPreviewDirtyState();
          } else {
            // A specific age was just selected, remove "All Ages"
            const filtered = value.filter((v: string) => v !== 'All Ages');
            ageRangeControl.setValue(filtered, { emitEvent: false });
            previousValue = filtered;
            this.syncPreviewDirtyState();
          }
        } else {
          // Update previous value for valid selections
          previousValue = [...value];
          this.syncPreviewDirtyState();
        }
      })
    );
  }

  private setupGenderListener(): void {
    const genderControl = this.createForm.get('gender');
    if (!genderControl) {
      return;
    }

    let previousValue = genderControl.value || ['All Gender'];

    this.subscriptions.add(
      genderControl.valueChanges.subscribe(value => {
        if (!Array.isArray(value)) {
          return;
        }

        // Prevent deselecting the last option - must always have at least one selected
        if (value.length === 0) {
          genderControl.setValue(previousValue, { emitEvent: false });
          return;
        }

        // If "All Gender" is selected along with other options
        if (value.includes('All Gender') && value.length > 1) {
          // Check if "All Gender" was just added (last in array) or was already there
          const lastAdded = value[value.length - 1];
          if (lastAdded === 'All Gender') {
            // "All Gender" was just selected, remove all other gender filters
            genderControl.setValue(['All Gender'], { emitEvent: false });
            previousValue = ['All Gender'];
            this.syncPreviewDirtyState();
          } else {
            // A specific gender was just selected, remove "All Gender"
            const filtered = value.filter((v: string) => v !== 'All Gender');
            genderControl.setValue(filtered, { emitEvent: false });
            previousValue = filtered;
            this.syncPreviewDirtyState();
          }
        } else {
          // Update previous value for valid selections
          previousValue = [...value];
          this.syncPreviewDirtyState();
        }
      })
    );
  }

  private setupRegionsListener(): void {
    const regionsControl = this.createForm.get('regions');
    if (!regionsControl) {
      return;
    }

    let previousValue = regionsControl.value || ['All Regions'];

    this.subscriptions.add(
      regionsControl.valueChanges.subscribe(value => {
        if (!Array.isArray(value)) {
          return;
        }

        // Only apply logic when region selection is enabled (Italy selected)
        if (!this.regionSelectionEnabled) {
          return;
        }

        // Prevent deselecting the last option - must always have at least one selected
        if (value.length === 0) {
          regionsControl.setValue(previousValue, { emitEvent: false });
          return;
        }

        // If "All Regions" is selected along with other options
        if (value.includes('All Regions') && value.length > 1) {
          // Check if "All Regions" was just added (last in array) or was already there
          const lastAdded = value[value.length - 1];
          if (lastAdded === 'All Regions') {
            // "All Regions" was just selected, remove all other region filters
            regionsControl.setValue(['All Regions'], { emitEvent: false });
            previousValue = ['All Regions'];
            this.syncPreviewDirtyState();
          } else {
            // A specific region was just selected, remove "All Regions"
            const filtered = value.filter((v: string) => v !== 'All Regions');
            regionsControl.setValue(filtered, { emitEvent: false });
            previousValue = filtered;
            this.syncPreviewDirtyState();
          }
        } else {
          // Update previous value for valid selections
          previousValue = [...value];
          this.syncPreviewDirtyState();
        }
      })
    );
  }

  private setupPreviewInvalidation(): void {
    this.subscriptions.add(
      this.createForm.valueChanges.subscribe(() => {
        if (this.isInitializingForm) {
          return; // Skip during form initialization
        }
        this.syncInterestFiltersChanged();
        if (!this.previewCompositionSignature && !this.initialRelevantCompositionSignature) {
          return;
        }
        if (this.createForm.pristine) {
          this.initialRelevantCompositionSignature = this.buildPreviewCompositionSignature();
          this.previewDirty = false;
          return;
        }
        this.syncPreviewDirtyState();
      })
    );
  }

  private syncPreviewDirtyState(): void {
    if (this.isInitializingForm) {
      return;
    }
    this.syncInterestFiltersChanged();
    const baseline = this.initialRelevantCompositionSignature ?? this.previewCompositionSignature;
    if (!baseline) {
      return;
    }
    this.previewDirty = this.buildPreviewCompositionSignature() !== baseline;
  }

  private applyPreviewState(previewData: AudiencePreviewAccuracyView | null): void {
    console.log('[applyPreviewState] received previewData:', previewData);

    if (!previewData?.points?.length) {
      console.log('[applyPreviewState] no points, resetting');
      this.resetPreviewState();
      return;
    }

    const normalizedPreviewData = this.normalizePreviewData(previewData);
    if (!normalizedPreviewData?.points.length) {
      this.resetPreviewState();
      return;
    }

    this.previewData = normalizedPreviewData;
    const rawPreviewStep = this.createForm.get('previewAccuracyStep')?.value;
    const selectedStep = Math.min(
      Math.max(
        Number(
          rawPreviewStep === null || rawPreviewStep === undefined
            ? normalizedPreviewData.selectedStep ?? normalizedPreviewData.suggestedStep ?? normalizedPreviewData.defaultStep ?? this.previewSliderMin
            : this.resolvePreviewStepForVisiblePoints(previewData, rawPreviewStep, normalizedPreviewData.defaultStep)
        ),
        this.previewSliderMin
      ),
      Math.max(normalizedPreviewData.points.length - 1, this.previewSliderMin)
    );

    this.previewData = {
      ...normalizedPreviewData,
      selectedStep
    };
    this.previewAccuracyStepControl.setValue(selectedStep, { emitEvent: false });
    this.previewDirty = false;
    this.previewCompositionSignature = this.buildPreviewCompositionSignature();
  }

  private normalizePreviewData(previewData: AudiencePreviewAccuracyView): AudiencePreviewAccuracyView | null {
    const originalPoints = previewData.points || [];
    const suggestedDistance = previewData.suggestedStep !== undefined
      ? Number(originalPoints[previewData.suggestedStep]?.minDistance ?? NaN)
      : NaN;
    // Full-curve mode (admin): no clamping — show everything from the data.
    // Otherwise, if suggested point is below 0.1, start from 0 for context.
    const effectiveMin = this.showFullCurve
      ? 0
      : Number.isFinite(suggestedDistance) && suggestedDistance < AudienceCreateComponent.PREVIEW_MIN_DISTANCE
        ? 0
        : AudienceCreateComponent.PREVIEW_MIN_DISTANCE;
    const normalizedPoints = originalPoints
      .filter(point => Number(point.minDistance) >= effectiveMin)
      .map((point, index) => ({
        ...point,
        step: index
      }));

    if (!normalizedPoints.length) {
      return null;
    }

    const resolveStep = (rawStep: number | undefined, fallbackStep: number): number => {
      return this.resolvePreviewStepBetweenPointSets(originalPoints, normalizedPoints, rawStep, fallbackStep);
    };

    const defaultStep = resolveStep(previewData.defaultStep, 0);
    const selectedStep = resolveStep(previewData.selectedStep, defaultStep);
    const suggestedStep = previewData.suggestedStep === undefined
      ? undefined
      : resolveStep(previewData.suggestedStep, defaultStep);

    return {
      ...previewData,
      points: normalizedPoints,
      defaultStep,
      selectedStep,
      suggestedStep
    };
  }

  private resolvePreviewStepForVisiblePoints(
    previewData: AudiencePreviewAccuracyView,
    rawStep: number,
    fallbackStep: number
  ): number {
    const originalPoints = previewData.points || [];
    const visiblePoints = this.previewData?.points || [];

    if (!visiblePoints.length) {
      return fallbackStep;
    }

    return this.resolvePreviewStepBetweenPointSets(originalPoints, visiblePoints, rawStep, fallbackStep);
  }

  private resolvePreviewStepBetweenPointSets(
    sourcePoints: AudiencePreviewPointView[],
    targetPoints: AudiencePreviewPointView[],
    rawStep: number | undefined,
    fallbackStep: number
  ): number {
    const sourceIndex = Math.min(Math.max(Number(rawStep ?? fallbackStep) || 0, 0), Math.max(sourcePoints.length - 1, 0));
    const targetDistance = sourcePoints[sourceIndex]?.minDistance ?? targetPoints[0]?.minDistance ?? 0;
    let closestIndex = 0;
    let closestDistance = Infinity;

    targetPoints.forEach((point, index) => {
      const distance = Math.abs(point.minDistance - targetDistance);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  private resetPreviewState(): void {
    this.previewData = null;
    this.previewDirty = false;
    this.previewCompositionSignature = null;
    this.initialRelevantCompositionSignature = null;
    this.previewAccuracyStepControl.setValue(null, { emitEvent: false });
  }

  private normalizeSelectionValues(value: unknown, allLabel: string): string[] {
    const entries = Array.isArray(value)
      ? value.map(item => (item ?? '').toString().trim()).filter(Boolean)
      : [];
    if (!entries.length || entries.includes(allLabel)) {
      return [allLabel];
    }
    return [...entries].sort((a, b) => a.localeCompare(b));
  }

  private normalizeTagsSignature(value: unknown): string[] {
    return Array.isArray(value)
      ? [...value].map(tag => (tag ?? '').toString().trim()).filter(Boolean).sort((a, b) => a.localeCompare(b))
      : [];
  }

  // Firma dei soli filtri che alimentano la curva di un'audience "interests".
  private buildInterestFiltersSignature(): string {
    return JSON.stringify({
      tags: this.normalizeTagsSignature(this.aiTagsControl.value),
      ageRange: this.normalizeSelectionValues(this.createForm.get('ageRange')?.value, 'All Ages'),
      gender: this.normalizeSelectionValues(this.createForm.get('gender')?.value, 'All Gender'),
      location: this.createForm.get('location')?.value ?? null,
      regions: this.normalizeSelectionValues(this.createForm.get('regions')?.value, 'All Regions')
    });
  }

  private syncInterestFiltersChanged(): void {
    if (this.isInitializingForm || !this.initialInterestFiltersSignature) {
      return;
    }
    this.interestFiltersChanged = this.isInterestBased
      && this.buildInterestFiltersSignature() !== this.initialInterestFiltersSignature;
  }

  private buildPreviewCompositionSignature(): string {
    const normalizeSelection = (value: unknown, allLabel: string): string[] =>
      this.normalizeSelectionValues(value, allLabel);

    const normalizeRuleBlocks = ((this.createForm.get('ruleBlocks')?.value ?? []) as any[]).map((block: any) => ({
      operator: block?.operator ?? 'AND',
      groups: ((block?.groups ?? []) as any[]).map((group: any) => ({
        type: group?.type ?? '',
        operator: group?.valueOperator ?? 'AND',
        payload: Array.isArray(group?.payload)
          ? [...group.payload].map((item: any) => (item ?? '').toString().trim()).filter(Boolean).sort((a: string, b: string) => a.localeCompare(b))
          : []
      }))
    }));

    if (this.isRuleBased) {
      return JSON.stringify({
        type: this.createForm.get('type')?.value ?? null,
        ruleBlocks: normalizeRuleBlocks
      });
    }

    return JSON.stringify({
      type: this.createForm.get('type')?.value ?? null,
      tags: this.normalizeTagsSignature(this.aiTagsControl.value),
      ageRange: normalizeSelection(this.createForm.get('ageRange')?.value, 'All Ages'),
      gender: normalizeSelection(this.createForm.get('gender')?.value, 'All Gender'),
      location: this.createForm.get('location')?.value ?? null,
      regions: normalizeSelection(this.createForm.get('regions')?.value, 'All Regions'),
      useAllNetworkData: this.createForm.get('useAllNetworkData')?.value ?? null
    });
  }

  getCurrentPreviewStep(): number | null {
    if (!this.previewData?.points?.length) {
      return null;
    }
    const raw = this.previewAccuracyStepControl.value
      ?? this.previewData?.selectedStep
      ?? this.previewData?.suggestedStep
      ?? this.previewData.defaultStep
      ?? this.previewSliderMin;
    return Math.min(Math.max(Number(raw) || 0, this.previewSliderMin), this.previewSliderMax);
  }

  private resetForm(): void {
    this.createForm.reset({
      type: AUDIENCE_TYPES.INTERESTS,
      name: '',
      description: '',
      frequency: 'Daily',
      startDate: this.getDefaultDate(),
      endDate: '',
      ongoing: true,
      aiDescription: '',
      useAllNetworkData: true,
      expandSimilarUsers: true,
      expandVersionWeighted: true,
      lookalikeMethod: 'knn',
      ageRange: ['All Ages'],
      gender: ['All Gender'],
      location: 'Italy',
      regions: ['All Regions'],
      audienceQuality: 'high',
      extendVolumes: null,
      previewAccuracyStep: null,
      aiTags: []
    });
    this.ruleBlocks.clear();
    this.addRuleBlock();
    this.updateEndDateControlState(true);
    this.applyLocationSelection(this.createForm.get('location')?.value || '');
    this.resetPreviewState();
    this.currentAudienceStatus = null;
    this.savedFrequency = null;
    this.syncFrequency();
    this.initialRelevantCompositionSignature = null;
    this.initialInterestFiltersSignature = null;
    this.interestFiltersChanged = false;
    this.expandVersionCurveUnavailable = false;
  }

  private applyInitialValue(value: Partial<AudienceCreateFormValue>): void {
    if (!value) {
      return;
    }

    // Set flag to prevent valueChanges subscription from resetting previewDirty
    this.isInitializingForm = true;

    // Conserva lo status dell'audience per la logica di visualizzazione
    this.currentAudienceStatus = value.audienceStatus || null;

    // Volume effettivo estratto (metadato che viaggia con initialValue), per l'Overview
    this.actualVolume = value.actualVolume ?? null;
    this.audienceId = value.audienceId ?? null;

    this.ruleBlocks.clear();
    const normalizedBlocks = (value.ruleBlocks && value.ruleBlocks.length)
      ? value.ruleBlocks
      : this.normalizeLegacyRuleGroups(value);

    if (!normalizedBlocks.length) {
      this.addRuleBlock();
    } else {
      normalizedBlocks.forEach((block, blockIndex) => {
        const blockGroup = this.createRuleBlock(block.operator || (blockIndex === 0 ? 'AND' : 'AND'));
        const groupsArray = blockGroup.get('groups') as FormArray<FormGroup>;
        (block.groups || []).forEach(group => {
          groupsArray.push(this.fb.group({
            type: [group.type],
            valueOperator: [group.valueOperator || 'AND'],
            payload: [this.normalizeGroupPayload(group.payload)]
          }));
        });
        this.ruleBlocks.push(blockGroup);
      });
    }

    const nextType = value.type ?? AUDIENCE_TYPES.INTERESTS;

    // Parse audienceQuality: può essere 'high'/'medium'/'low' oppure un numero (audience size)
    let parsedAudienceQuality: AudienceQuality | null = null;
    let parsedExtendVolumes: number | null = null;

    if (value.audienceQuality !== null && value.audienceQuality !== undefined) {
      const qualityValue = value.audienceQuality;
      // Se è un numero, va in extendVolumes
      if (typeof qualityValue === 'number' || !isNaN(Number(qualityValue))) {
        parsedExtendVolumes = Number(qualityValue);
        parsedAudienceQuality = null;
      }
      // Se è una stringa valida ('high', 'medium', 'low'), va in audienceQuality
      else if (qualityValue === 'high' || qualityValue === 'medium' || qualityValue === 'low') {
        parsedAudienceQuality = qualityValue;
        parsedExtendVolumes = null;
      }
    }

    // Se extendVolumes è già presente, ha precedenza
    if (value.extendVolumes !== null && value.extendVolumes !== undefined) {
      parsedExtendVolumes = value.extendVolumes;
      parsedAudienceQuality = null;
    }

    // Per (custom) Rule Based: se audienceQuality è popolato (string o number), expandSimilarUsers deve essere true
    const hasAudienceQuality = (parsedAudienceQuality !== null) ||
                                (parsedExtendVolumes !== null && parsedExtendVolumes > 0);
    const shouldExpandSimilarUsers = value.expandSimilarUsers ??
                                     (nextType === AUDIENCE_TYPES.CUSTOM ? hasAudienceQuality : true);

    this.createForm.patchValue({
      type: nextType,
      name: value.name ?? '',
      description: value.description ?? '',
      frequency: value.frequency ?? 'Daily',
      startDate: value.startDate ?? this.getDefaultDate(),
      endDate: value.endDate ?? '',
      ongoing: value.ongoing ?? this.isForeverEndDate(value.endDate),
      aiDescription: value.aiDescription ?? '',
      useAllNetworkData: value.useAllNetworkData ?? true,
      expandSimilarUsers: shouldExpandSimilarUsers,
      expandVersionWeighted: value.expandVersionWeighted ?? true,
      lookalikeMethod: value.lookalikeMethod ?? 'knn',
      ageRange: value.ageRange ?? [],
      gender: value.gender ?? [],
      location: this.normalizeLocationValue(value.location),
      regions: value.regions ?? [],
      audienceQuality: parsedAudienceQuality,
      extendVolumes: parsedExtendVolumes,
      previewAccuracyStep: value.previewAccuracyStep ?? null,
      aiTags: value.aiTags ?? [],
      isPublic: value.isPublic ?? false
    });
    this.savedFrequency = (this.mode !== 'create' || this.currentAudienceStatus) ? (value.frequency ?? null) : null;
    this.syncFrequency();
    this.updateEndDateControlState(!!this.createForm.get('ongoing')?.value);
    this.applyLocationSelection(this.createForm.get('location')?.value || '');
    this.showFullCurve = false;
    this.expandVersionCurveUnavailable = false;
    this.normalPreviewData = value.previewData ?? null;
    this.fullRangePreviewDataStore = value.fullRangePreviewData ?? null;
    this.applyPreviewState(this.normalPreviewData);

    console.log('[applyInitialValue] Initial state check:', {
      audienceStatus: value.audienceStatus,
      hasPreviewData: !!value.previewData,
      previewDataPointsCount: value.previewData?.points?.length,
      type: value.type,
      expandSimilarUsers: value.expandSimilarUsers,
      currentPreviewDirty: this.previewDirty
    });

    // Log finale dello stato dopo l'inizializzazione
    console.log('[applyInitialValue] Final state:', {
      currentAudienceStatus: this.currentAudienceStatus,
      previewDirty: this.previewDirty,
      previewData: !!this.previewData,
      isInterestBased: this.isInterestBased,
      isRuleBased: this.isRuleBased,
      expandSimilarUsers: this.expandSimilarUsers,
      requiresPreviewFlow: this.requiresPreviewFlow,
      isPreviewReady: this.isPreviewReady,
      shouldShowAccuracyPreview: this.shouldShowAccuracyPreview,
      shouldShowScheduleSection: this.shouldShowScheduleSection,
      shouldShowSaveButton: this.shouldShowSaveButton,
      shouldShowPreviewButton: this.shouldShowPreviewButton
    });

    this.createForm.markAsPristine();
    this.createForm.markAsUntouched();

    // Reset flag after all subscriptions have been processed
    setTimeout(() => {
      this.initialRelevantCompositionSignature = this.buildPreviewCompositionSignature();
      // Baseline dei filtri "interests" così com'erano all'apertura della modale: serve a
      // nascondere la curva quando cambiano e a rimostrarla se si torna esattamente a questi valori.
      this.initialInterestFiltersSignature = this.buildInterestFiltersSignature();
      this.interestFiltersChanged = false;
      if (value.previewData) {
        this.previewDirty = false;
        this.previewCompositionSignature = this.buildPreviewCompositionSignature();
        console.log('[applyInitialValue] Existing preview data loaded');
      }
      this.isInitializingForm = false;
      this.initialValueApplied = true;
      this.staleItemsEmitted = false;
      this.createForm.markAsPristine();
      this.createForm.markAsUntouched();
      console.log('[applyInitialValue] Initialization complete, flag reset');
      this.checkAndEmitStaleItems();
    }, 0);
  }

  private getDefaultDate(): string {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date.toISOString().slice(0, 10);
  }

  private isForeverEndDate(value?: string | null): boolean {
    if (!value) {
      return true;
    }

    return value >= '2050-11-30';
  }

  private rebuildSelectOptions(): void {
    this.frequencyOptions = this.toSelectOptions(this.availableFrequencies);
    this.ageOptions = this.toSelectOptions(this.ageRanges, 'age');
    this.genderOptions = this.toSelectOptions(this.genders, 'gender');
    this.lookalikeMethodOptions = LOOKALIKE_METHODS.map(method => ({
      value: method,
      label: this.translate.instant(`audiences.create.lookalikeMethod.${method}`)
    }));
  }

  private toSelectOptions(values: string[], kind: 'generic' | 'age' | 'gender' = 'generic'): CustomSelectOption[] {
    return values.map(value => ({
      value,
      label: this.getSelectLabel(value, kind)
    }));
  }

  getAgeSummaryLabel(values?: string[] | null): string {
    return this.formatSummaryValues(values, 'age');
  }

  getGenderSummaryLabel(values?: string[] | null): string {
    return this.formatSummaryValues(values, 'gender');
  }

  private getSelectLabel(value: string, kind: 'generic' | 'age' | 'gender'): string {
    const map = kind === 'age'
      ? this.ageLabelMap
      : kind === 'gender'
        ? this.genderLabelMap
        : null;
    if (map) {
      const key = map[value];
      if (key) {
        const translated = this.translate.instant(key);
        if (translated && translated !== key) {
          return translated;
        }
      }
    }
    return value;
  }

  private formatSummaryValues(values: string[] | null | undefined, kind: 'age' | 'gender'): string {
    if (!values || values.length === 0) {
      return '—';
    }
    return values.map(value => this.getSelectLabel(value, kind)).join(', ');
  }

  private readonly ageLabelMap: Record<string, string> = {
    'All Ages': 'audiences.create.filters.ageAll',
    'Unknown': 'audiences.create.filters.ageUnknown'
  };

  private readonly genderLabelMap: Record<string, string> = {
    'All Gender': 'audiences.create.filters.genderAll',
    'Unknown': 'audiences.create.filters.genderUnknown'
  };

  private buildOperatorOptions(): CustomSelectOption[] {
    return this.logicOperators.map((operator) => ({
      value: operator,
      label: this.getOperatorLabel(operator)
    }));
  }

  private getOperatorLabel(operator?: string | null): string {
    const fallback = 'AND';
    const normalized = this.normalizePreviewOperator(operator || fallback);
    const keySuffix = normalized.toLowerCase().replace(/\s+/g, '_');
    const key = `audiences.create.rule.operator.${keySuffix}`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) {
      return translated;
    }
    return normalized;
  }

  private normalizePreviewOperator(operator?: string | null): LogicalOperator {
    const normalized = (operator || '').toString().trim().toUpperCase();
    if (normalized === 'OR') {
      return 'OR';
    }
    if (normalized.includes('NOT')) {
      return 'AND NOT';
    }
    return 'AND';
  }

  private normalizeLocationValue(value?: string | null): string {
    const fallback = this.translate.instant('audiences.create.filters.locationAll') || this.defaultLocationLabel;
    this.defaultLocationLabel = fallback;
    const trimmed = (value || '').toString().trim();
    if (!trimmed.length) {
      return fallback;
    }
    const normalized = trimmed.toLowerCase();
    if (normalized === fallback.toLowerCase() || normalized === 'all locations') {
      return fallback;
    }
    return trimmed;
  }

  private loadGoals(): void {
    this.subscriptions.add(
      this.audienceApi.getGoals('active').pipe(
        catchError(error => {
          console.error('[audience-create] goals load failed', error);
          return of<GoalOption[]>([]);
        })
      ).subscribe(goals => {
        this.goalOptions = goals.map(goal => ({
          id: goal?.ID ?? '',
          name: goal?.Description || goal?.ID || '',
          sourceGroupId: goal?.SourceGroupID ?? null
        })).filter(goal => !!goal.id && !!goal.name);
        this.goalsLoaded = true;
        this.checkAndEmitStaleItems();
      })
    );
  }

  private applyLocationSelection(value: string): void {
    const normalizedValue = this.normalizeLocationValue(value);
    const match = this.findCountryByName(normalizedValue);
    if (match && match.code === this.defaultLocationCode) {
      if (this.selectedCountryCode === 'IT') {
        this.clearRegions();
      }
      this.selectedCountryCode = null;
      this.createForm.get('location')?.setValue(match.name, { emitEvent: false });
      this.closeRegionDropdown();
      this.syncRegionControlDisabledState();
      return;
    }
    if (!match) {
      if (!(this.countryOptions ?? []).length) {
        // Real country list hasn't arrived from the parent yet (e.g. editing an audience
        // right after this component was created): keep the raw value as-is instead of
        // wiping it out to "All Locations". ngOnChanges re-runs this once countryOptions lands.
        this.createForm.get('location')?.setValue(normalizedValue, { emitEvent: false });
        return;
      }
      this.selectedCountryCode = null;
      this.createForm.get('location')?.setValue(this.defaultLocationLabel, { emitEvent: false });
      this.clearRegions();
      this.closeRegionDropdown();
      this.syncRegionControlDisabledState();
      return;
    }
    const nextCode = match.code;
    const previousCode = this.selectedCountryCode;
    this.selectedCountryCode = nextCode;
    this.createForm.get('location')?.setValue(match.name, { emitEvent: false });
    if (nextCode === 'IT') {
      if (previousCode !== 'IT' || this.loadedRegionCountryCode !== 'IT') {
        this.loadRegions('IT');
      }
    } else {
      // Non-Italy countries don't support region-level targeting: keep the field
      // disabled (see regionSelectionEnabled) and its value pinned to "All Regions".
      this.setRegionsToAll();
      this.closeRegionDropdown();
    }
    this.syncRegionControlDisabledState();
  }

  private setRegionsToAll(): void {
    this.loadedRegionCountryCode = null;
    const current = this.regionsControl.value ?? [];
    if (current.length !== 1 || current[0] !== 'All Regions') {
      this.regionsControl.setValue(['All Regions']);
    }
  }

  // Disables/enables the regions FormControl itself (not just the [disabled] template input),
  // so the custom-select's ControlValueAccessor.setDisabledState() stays the single source of
  // truth and can't be silently overridden by a separate @Input binding.
  private syncRegionControlDisabledState(): void {
    if (this.regionSelectionEnabled) {
      if (this.regionsControl.disabled) {
        this.regionsControl.enable({ emitEvent: false });
      }
    } else if (this.regionsControl.enabled) {
      this.regionsControl.disable({ emitEvent: false });
    }
  }

  private findCountryByName(value: string): CountryOption | undefined {
    const term = (value || '').trim().toLowerCase();
    if (!term) {
      return undefined;
    }
    return this.selectableCountryOptions.find(option => option.name.toLowerCase() === term);
  }

  private clearRegions(): void {
    this.loadedRegionCountryCode = null;
    if (this.regionsControl.value?.length) {
      this.regionsControl.setValue([]);
    }
  }

  private loadRegions(countryCode: string): void {
    this.loadedRegionCountryCode = countryCode;
    this.audienceApi.getCountryRegions(countryCode).pipe(
      catchError(error => {
        console.error('[audience-create] regions load failed', error);
        return of<string[]>([...this.italyRegions.filter(r => r !== 'All Regions')]);
      })
    ).subscribe((regions) => {
      const apiRegions = regions.length ? regions : [...this.italyRegions.filter(r => r !== 'All Regions')];
      // Always add "All Regions" as the first option
      const nextRegions = ['All Regions', ...apiRegions];
      const previousRegions = this.regionList;
      this.regionList = nextRegions;
      const current = this.regionsControl.value || [];

      // Check if "All Regions" was selected or all previous regions were selected
      const hadAllRegions = current.includes('All Regions') ||
                           (current.length > 0 &&
                            previousRegions.length > 0 &&
                            current.length === previousRegions.length &&
                            previousRegions.every((r: string) => current.includes(r)));

      if (hadAllRegions) {
        // If "All Regions" was selected, keep it selected
        this.regionsControl.setValue(['All Regions']);
      } else if (current.length) {
        // Filter to keep only valid regions
        const filtered = current.filter(region => nextRegions.includes(region));
        // Ensure at least one region is selected
        this.regionsControl.setValue(filtered.length > 0 ? filtered : ['All Regions']);
      } else {
        // Default to "All Regions" if nothing was selected
        this.regionsControl.setValue(['All Regions']);
      }
    });
  }

}
